// What the Legal review box and its side panel share: which file the panel
// shows, the filters (one set for both sides), what is open, what is new, and
// the person's own choices (language, panel width, full screen, the drag tip),
// which are remembered in this browser.
//
// The findings themselves live in legalCheckStore (the server's answer); this
// is only how they are being looked at.

import { useEffect, useState } from 'react';

const store = {
  get(k, d) { try { const v = window.localStorage.getItem(k); return v === null ? d : v; } catch (e) { return d; } },
  set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private window */ } },
};

const K = { lang: 'legalReview.lang', width: 'legalReview.width', full: 'legalReview.full', tip: 'legalReview.tipSeen' };

const state = {
  lang: store.get(K.lang, 'id') === 'en' ? 'en' : 'id',
  width: store.get(K.width, ''),            // '' = the default, 70% of the screen
  full: store.get(K.full, '0') === '1',
  tipSeen: store.get(K.tip, '') === '1',
  tip: false,                               // the drag tip showing now
  panel: { open: false, source: null, uid: null },
  views: {},                                // uid -> per-file view, see view()
  toast: null,                              // { text, n }
  version: 0,
};

const listeners = new Set();
const emit = () => { state.version += 1; listeners.forEach((fn) => fn(state.version)); };

export function useReview() {
  const [, setV] = useState(state.version);
  useEffect(() => {
    listeners.add(setV);
    return () => listeners.delete(setV);
  }, []);
  return state;
}

// One file's view: the filters, the box's page, what is open in the panel, the
// sections shown whole, the minor findings shown or not, and which findings
// arrived after the person first saw the list (marked new until opened).
export function view(uid) {
  if (!state.views[uid]) {
    state.views[uid] = {
      level: 'all', source: 'all', boxPage: 1, opened: new Set(), active: null,
      whole: new Set(), minor: false, seen: null, fresh: new Set(),
    };
  }
  return state.views[uid];
}

export function setLang(lang) {
  state.lang = lang === 'en' ? 'en' : 'id';
  store.set(K.lang, state.lang);
  emit();
}

export function setWidth(px, full) {
  state.width = px || '';
  state.full = !!full;
  store.set(K.width, state.width);
  store.set(K.full, state.full ? '1' : '0');
  emit();
}

export function setFull(full) {
  state.full = !!full;
  store.set(K.full, state.full ? '1' : '0');
  emit();
}

export function openPanel(source, uid, findingKey) {
  state.panel = { open: true, source, uid };
  if (!state.tipSeen) state.tip = true;
  if (findingKey) focus(uid, findingKey, false);
  emit();
}

export function closePanel() {
  state.panel = { ...state.panel, open: false };
  // Not "seen": the tip comes back next time until they press Mengerti or drag.
  state.tip = false;
  emit();
}

export function dismissTip() {
  if (!state.tip) return;
  state.tip = false;
  state.tipSeen = true;
  store.set(K.tip, '1');
  emit();
}

// Level and source combine; a click on a picked tab lets it go, Semua clears
// both. Every click starts from the clean overview: everything closed.
export function pickFilter(uid, k, levels) {
  const v = view(uid);
  if (k === 'all') { v.level = 'all'; v.source = 'all'; }
  else if (levels.includes(k)) v.level = v.level === k ? 'all' : k;
  else v.source = v.source === k ? 'all' : k;
  v.opened = new Set();
  v.active = null;
  v.boxPage = 1;
  emit();
}

export function setBoxPage(uid, page) { view(uid).boxPage = page; emit(); }

// One finding open at a time (Ivan, 2026-10-12): opening one closes whichever
// was open, from the list, from the box or with Berikutnya. It is marked seen.
export function focus(uid, key, notify = true) {
  const v = view(uid);
  v.opened = new Set([key]);
  v.active = key;
  v.fresh.delete(key);
  if (notify) emit();
}

// A click on a finding's line: opens it (closing any other), or closes it.
// Returns true when it opened.
export function toggle(uid, key) {
  const v = view(uid);
  const opening = !v.opened.has(key);
  if (opening) { v.opened = new Set([key]); v.active = key; v.fresh.delete(key); }
  else { v.opened = new Set(); v.active = null; }
  emit();
  return opening;
}

export function toggleWhole(uid, key) {
  const v = view(uid);
  if (v.whole.has(key)) v.whole.delete(key); else v.whole.add(key);
  emit();
}

export function toggleMinor(uid) { const v = view(uid); v.minor = !v.minor; emit(); }

export function resetFilters(uid) {
  const v = view(uid);
  v.level = 'all';
  v.source = 'all';
  emit();
}

// Which findings are new: the first list the person sees is the baseline;
// anything that arrives after (the AI read, the comparison) is new until opened.
// Returns how many became new this time.
export function noteKeys(uid, keys) {
  const v = view(uid);
  if (!v.seen) { v.seen = new Set(keys); return 0; }
  let added = 0;
  keys.forEach((k) => {
    if (!v.seen.has(k)) { v.seen.add(k); v.fresh.add(k); added += 1; }
  });
  if (added) emit();
  return added;
}

export function forget(uid) { delete state.views[uid]; }

let toastTimer = null;
export function toast(text) {
  state.toast = { text, n: (state.toast ? state.toast.n : 0) + 1 };
  emit();
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { state.toast = null; emit(); }, 2600);
}
